import { gql } from '@apollo/client';
import {
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import { PropsWithChildren } from 'react';
import { CellProps } from 'react-table';
import { useAuth } from '~auth/useAuth';
import { DeleteSchedulesModal } from '~components/schedules/DeleteSchedulesModal';
import { SyncScheduleDisplaysModal } from '~components/schedules/SyncScheduleDisplaysModal';
import { useRemoveScheduleFromDisplays } from '~components/schedules/useRemoveScheduleFromDisplays';
import { ActionMenuButton } from '~components/ui/ActionMenuButton';
import { CopyIcon, LoadingIcon, NoDisplayIcon, SyncIcon, TrashIcon } from '~components/ui/icons';
import { StopClickPropagation } from '~components/ui/StopClickPropagation';
import { fromError } from '~utils/errors';
import { ScheduleTable_ScheduleFragment } from '../__generated__/ScheduleTable.graphql';
import {
  ActionsCell_ScheduleFragment,
  useScheduleDuplicateMutation,
} from './__generated__/ActionsCell.graphql';

export function ActionsCell({
  value,
}: PropsWithChildren<CellProps<ScheduleTable_ScheduleFragment, ActionsCell_ScheduleFragment>>) {
  const deleteScheduleModal = useDisclosure();
  const syncDisplaysModal = useDisclosure();
  const { verifyUserPermissions } = useAuth();
  const toast = useToast();

  const hasScheduleCreatePermission = verifyUserPermissions([Permission.PowerscheduleCreate]);
  const hasScheduleDeletePermission = verifyUserPermissions([Permission.PowerscheduleDelete]);
  const hasDisplayContentUpdatePermission = verifyUserPermissions([
    Permission.DisplayContentUpdate,
  ]);

  const isRemoving = value.removingDisplays.length > 0;
  const isSyncing = value.syncingDisplays.length > 0;
  const isScheduleUpdatingOnDisplays = isSyncing || isRemoving;
  const syncOperationLabel = t('updateProgress');
  const hasOutOfSyncDisplays = value.outOfSyncDisplays.length > 0;

  const { removeScheduleFromDisplays, loading: isInitiatingRemove } =
    useRemoveScheduleFromDisplays();

  const [duplicateSchedule, { loading: isDuplicating }] = useScheduleDuplicateMutation();

  const handleDuplicate = async () => {
    try {
      const { data } = await duplicateSchedule({
        variables: { input: { scheduleId: value.id } },
        refetchQueries: ['CustomerSchedulesPage'],
        awaitRefetchQueries: true,
      });

      if (!data || !data.scheduleDuplicate.schedule) {
        throw new Error('Duplicate schedule failed');
      }

      toast({ status: 'success', title: t('scheduleDuplicated') });
    } catch (err) {
      toast({
        status: 'error',
        title: t('scheduleDuplicateErr'),
        description: fromError(err, 'ScheduleDuplicate', {
          SCHEDULE_LIMIT_REACHED: t('scheduleErrDesc'),
          WAVE_SUBSCRIPTION_SCHEDULES_LIMIT_REACHED: t('scheduleErrDesc2'),
        }),
      });
    }
  };

  return (
    <>
      <StopClickPropagation>
        <Flex justifyContent="center">
          {isScheduleUpdatingOnDisplays || isInitiatingRemove ? (
            <Tooltip label={syncOperationLabel}>
              <Flex
                aria-label={syncOperationLabel}
                justifyContent="center"
                alignItems="center"
                height="8"
                minWidth="8"
              >
                <LoadingIcon color="gray.400" height={5} width={5} />
              </Flex>
            </Tooltip>
          ) : (
            <Menu placement="bottom-end">
              <MenuButton as={ActionMenuButton} />
              <MenuList>
                <MenuItem
                  icon={<CopyIcon />}
                  isDisabled={!hasScheduleCreatePermission || isDuplicating}
                  onClick={handleDuplicate}
                >
                  {t('duplicate')}
                </MenuItem>

                <MenuItem
                  icon={<SyncIcon />}
                  onClick={syncDisplaysModal.onOpen}
                  isDisabled={!hasDisplayContentUpdatePermission || !hasOutOfSyncDisplays}
                >
                  {t('sync')}
                </MenuItem>
                <MenuItem
                  icon={<NoDisplayIcon />}
                  onClick={() => removeScheduleFromDisplays(value.id)}
                  isDisabled={!hasDisplayContentUpdatePermission || value.allDisplays.length === 0}
                >
                  {t('removeFromDisplays')}
                </MenuItem>
                <MenuItem
                  color={'red.500'}
                  icon={<TrashIcon color="red.500" />}
                  onClick={deleteScheduleModal.onOpen}
                  isDisabled={!hasScheduleDeletePermission}
                >
                  {t('delete')}
                </MenuItem>
              </MenuList>
            </Menu>
          )}
        </Flex>
      </StopClickPropagation>
      <SyncScheduleDisplaysModal
        isOpen={syncDisplaysModal.isOpen}
        schedule={value}
        onCancel={syncDisplaysModal.onClose}
        onSuccess={syncDisplaysModal.onClose}
      />
      <DeleteSchedulesModal
        isOpen={deleteScheduleModal.isOpen}
        schedules={[value]}
        onCancel={deleteScheduleModal.onClose}
        onSuccess={deleteScheduleModal.onClose}
      />
    </>
  );
}

ActionsCell.graphql = {
  fragments: {
    ActionsCell_schedule: gql`
      fragment ActionsCell_schedule on Schedule {
        id
        isScheduleUsedByAnyConfiguration
        removingDisplays: displays(filter: { state: REMOVING }) {
          id
        }
        syncingDisplays: displays(filter: { state: SYNCING }) {
          id
        }
        outOfSyncDisplays: displays(filter: { state: OUT_OF_SYNC }) {
          id
        }
        allDisplays: displays {
          id
        }
        ...useRemoveScheduleFromDisplays_schedule
        ...SyncScheduleDisplaysModal_schedule
        ...DeleteSchedulesModal_schedule
      }
    `,
  },
  mutations: {
    ScheduleDuplicate: gql`
      mutation ScheduleDuplicate($input: ScheduleDuplicateInput!) {
        scheduleDuplicate(input: $input) {
          schedule {
            id
          }
        }
      }
    `,
  },
};
